import type {
  LeadsRegistryFilters,
  LeadsRegistryChatScope,
  LeadsRegistryLoadBatchSize,
  LeadsRegistryPeriodType,
  LeadsRegistrySearchType,
} from './types';

export const LEADS_REGISTRY_EMPTY_VALUE = 'Нет данных';

export const LEADS_REGISTRY_TABLE_SCROLL_X = 1395;

export const LEADS_REGISTRY_TABLE_MIN_SCROLL_Y = 360;

export const LEADS_REGISTRY_ROW_CONTENT_HEIGHT = 52;

export const LEADS_REGISTRY_ROW_VERTICAL_PADDING = 7;

export const LEADS_REGISTRY_ROW_HEIGHT =
  LEADS_REGISTRY_ROW_CONTENT_HEIGHT + LEADS_REGISTRY_ROW_VERTICAL_PADDING * 2;

export const LEADS_REGISTRY_TABLE_DESKTOP_SCROLL_OFFSET = 300;

export const LEADS_REGISTRY_TABLE_MOBILE_SCROLL_OFFSET = 240;

export const LEADS_INITIAL_LOADING_SKELETON_COUNT = 12;

export const MIN_LEADS_PORTION_SIZE = 20;

export const DEFAULT_LEADS_LOAD_BATCH_SIZE = MIN_LEADS_PORTION_SIZE;

export const LEADS_REGISTRY_LOAD_BATCH_SIZE_OPTIONS: LeadsRegistryLoadBatchSize[] =
  [20, 50, 100];

// Funnels board: user-configurable maximum number of stage columns shown at
// once. The actual count is additionally capped by the viewport width and the
// number of stages (see useLeadsFunnelBoardColumns). Default keeps the current
// "show as many stages as fit" behavior.
export const LEADS_FUNNEL_MIN_VISIBLE_COLUMNS = 1;

export const LEADS_FUNNEL_MAX_VISIBLE_COLUMNS = 10;

export const DEFAULT_LEADS_FUNNEL_MAX_VISIBLE_COLUMNS =
  LEADS_FUNNEL_MAX_VISIBLE_COLUMNS;

export const LEADS_FUNNEL_MAX_VISIBLE_COLUMN_OPTIONS: number[] = Array.from(
  { length: LEADS_FUNNEL_MAX_VISIBLE_COLUMNS },
  (_, index) => index + 1
);

export const LEADS_LOADING_SKELETON_COUNT = 3;

export const LEADS_SCROLL_LOAD_THRESHOLD_PX = 72;

export const DEFAULT_LEADS_REGISTRY_SEARCH_TYPE: LeadsRegistrySearchType =
  'phones';

export const LEADS_REGISTRY_SEARCH_TYPE_OPTIONS: {
  value: LeadsRegistrySearchType;
  label: string;
}[] = [
  { value: 'messages', label: 'В сообщениях' },
  { value: 'phones', label: 'По телефону' },
  { value: 'names', label: 'По ФИО' },
  { value: 'usernames', label: 'По никнейму в соц.сетях' },
];

// Minimum search length before the term is sent to `SearchLeads`.
// messages mirrors the legacy registry (5 chars); phones/names/usernames
// thresholds (6/4/4) are per product requirements for this feature (task/10485).
export const LEADS_REGISTRY_SEARCH_MIN_LENGTH: Record<
  LeadsRegistrySearchType,
  number
> = {
  messages: 5,
  phones: 6,
  names: 4,
  usernames: 4,
};

// Placeholder hint for the search input, surfacing the per-type minimum
// length without an intrusive tooltip/message that would pop in and out
// as the user types (task/10554, follow-up to task/10485).
export const getLeadsRegistrySearchPlaceholder = (
  searchType: LeadsRegistrySearchType
): string =>
  `Поиск, от ${LEADS_REGISTRY_SEARCH_MIN_LENGTH[searchType]} символов`;

export const LEADS_REGISTRY_DEFAULT_TAGS_GROUP_ID = 'g1';

export const DEFAULT_LEADS_REGISTRY_PERIOD_TYPE: LeadsRegistryPeriodType =
  'activity';

export const LEADS_REGISTRY_PERIOD_TYPE_OPTIONS: {
  value: LeadsRegistryPeriodType;
  label: string;
}[] = [
  { value: 'activity', label: 'В работе' },
  { value: 'created', label: 'Созданы' },
];

export const getDefaultLeadsRegistryFilters = (): LeadsRegistryFilters => ({
  search: '',
  searchType: DEFAULT_LEADS_REGISTRY_SEARCH_TYPE,
  branches: [],
  channels: [],
  integrations: [],
  users: [],
  statuses: [],
  crmTaskStatuses: [],
  tags: {
    groups: [
      {
        id: LEADS_REGISTRY_DEFAULT_TAGS_GROUP_ID,
        logic: 'AND',
        selectedValues: [],
      },
    ],
  },
  salesFunnelId: null,
  funnelStages: [],
  periodType: DEFAULT_LEADS_REGISTRY_PERIOD_TYPE,
  // The period is optional: unset by default, the backend falls back to the
  // last 30 days of activity when no dates are sent.
  datePreset: null,
  dates: [null, null],
});

export const LEADS_REGISTRY_NO_RESPONSIBLE_OPTION_ID = '0';

export const LEADS_REGISTRY_CHAT_SCOPE_OPTIONS: {
  value: LeadsRegistryChatScope;
  label: string;
}[] = [
  { value: 'all', label: 'Все чаты' },
  { value: 'mine', label: 'Мои чаты' },
  { value: 'free', label: 'Свободны' },
];
